import type { RefObject } from "react";
import { useRef, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { PRODUCT_TAB } from "~/components/blocks/product/common/video-season/video-season-tab/video-season-tab-contents/const";

import type { DownloadModalData } from "~/components/stores/detail-product/store";
import {
  clickedContentIdVar,
  isOpenDownloadModalVar,
  downloadModalDataVar,
} from "~/components/stores/detail-product/store";
import { useMatchMedia } from "~/hooks/use-match-media";
import { usePurchaseModalActions } from "~/hooks/use-purchase-modal-actions";
import { useQueryString } from "~/hooks/use-query-string";
import { useGetVideoPurchasedEpisodeLazyQuery } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { getPlayDevice } from "~/util/get-device";

const FIRST_COUNT = 8;

/** fetch moreする領域、heightに対して30%の値を設定 */
const NEXT_FETCH_AREA = {
  PC: 240,
  SP: 162,
};

let isRunning = false;

type UsePurchasedArgs = {
  /** ref object */
  ref: RefObject<HTMLDivElement>;
  /** 購入済みの合計数 */
  totalPurchasedLength: number;
  /** update fetch more state fn */
  onSetIsLoadingFetchMore: (isLoading: boolean) => void;
};

export const usePurchased = (args: UsePurchasedArgs) => {
  const { ref, totalPurchasedLength, onSetIsLoadingFetchMore } = args;

  const [exec, { loading: isVideoPurchasedEpisodeLoading, data: videoPurchasedEpisodeData, fetchMore }] =
    useGetVideoPurchasedEpisodeLazyQuery({ fetchPolicy: "network-only" });

  const { isReady } = useRouter();
  const { season } = useQueryString();
  const { isSpSize } = useMatchMedia();
  const { isLoggedIn } = useAuth();

  const nextFetchArea = useMemo(() => (isSpSize ? NEXT_FETCH_AREA.SP : NEXT_FETCH_AREA.PC), [isSpSize]);

  const itemHeight = useMemo(() => {
    return !isSpSize ? PRODUCT_TAB.PC_AND_TABLET.MIN_ITEM_HEIGHT : PRODUCT_TAB.SP.MIN_ITEM_HEIGHT;
  }, [isSpSize]);

  const imageWidth = useMemo(() => {
    return !isSpSize ? PRODUCT_TAB.PC_AND_TABLET.IMAGE_WIDTH : PRODUCT_TAB.SP.IMAGE_WIDTH;
  }, [isSpSize]);

  const imageHeight = useMemo(() => {
    return !isSpSize ? PRODUCT_TAB.PC_AND_TABLET.IMAGE_HEIGHT : PRODUCT_TAB.SP.IMAGE_HEIGHT;
  }, [isSpSize]);

  const { pushWithQueriesPurchaseModal } = usePurchaseModalActions();

  const purchasedEpisodeDownloadBtnClicked = (episodeId: string, downloadModalData: DownloadModalData) => {
    isOpenDownloadModalVar(true);
    clickedContentIdVar(episodeId);
    downloadModalDataVar(downloadModalData);
  };

  const episodePurchasedBtnClicked = (contentId: string) => {
    pushWithQueriesPurchaseModal(contentId);
  };

  const state = useRef<{
    hasNextPage?: boolean;
    endCursor?: string;
  }>();

  // MEMO（kimura）: ブラウザバックで`TypeError`にならないようにすべてオプショナルチェーンでドット記法で繋ぐ
  state.current = {
    ...videoPurchasedEpisodeData?.video?.purchasedContents?.pageInfo,
  };

  const onEndReached = async () => {
    if (!fetchMore || !state.current?.hasNextPage || !state.current?.endCursor) return;

    isRunning = true;
    onSetIsLoadingFetchMore(true);

    await fetchMore({
      variables: {
        id: season,
        device: getPlayDevice(),
        isLoggedIn,
        first: FIRST_COUNT,
        after: state.current?.endCursor,
      },
    });

    isRunning = false;
    onSetIsLoadingFetchMore(false);
  };

  const onScroll = () => {
    const scope = ref.current;
    if (!scope || isRunning) return;
    const { scrollTop, clientHeight, scrollHeight } = scope;
    const scrollBottom = scrollHeight - (scrollTop + clientHeight);
    if (scrollBottom < nextFetchArea) onEndReached();
  };

  useEffect(() => {
    if (isVideoPurchasedEpisodeLoading || totalPurchasedLength === 0) return;

    const scope = ref.current;
    if (!scope) return;
    scope.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      scope.removeEventListener("scroll", onScroll);
    };
  }, [isVideoPurchasedEpisodeLoading, totalPurchasedLength]);

  useEffect(() => {
    if (!isReady || season === undefined) return;

    const controller = new AbortController();

    void (async () => {
      void exec({
        variables: {
          id: season,
          device: getPlayDevice(),
          first: FIRST_COUNT,
          isLoggedIn,
        },
      });
    })();

    return () => controller.abort();
  }, [season, isReady]);

  const { edges: purchasedEdges } = videoPurchasedEpisodeData?.video?.purchasedContents ?? {};

  return {
    itemHeight,
    imageWidth,
    imageHeight,
    isVideoPurchasedEpisodeLoading,
    purchasedEdges,
    purchasedEpisodeDownloadBtnClicked,
    episodePurchasedBtnClicked,
  };
};
